import {FC, useState, useEffect} from 'react'
import {i18n} from '../../../translate/i18n'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Button} from '../../../components/Button/Button'
import {Input} from '../../../components/Input/Input'
import {HospitalityTabs, TabItem} from '../../../components/HospitalityTabs'
import Swal from 'sweetalert2'
import {POST, GET} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {t} from 'i18next'
import {EditorQuill} from '../../../components/EditorQuill/EditorQuill'

type ContactInfo = {
  phone: string
  whatsapp: string
  email: string
}

type HoursInfo = {
  recepção: string
  restaurante: string
  piscina: string
  spa: string
  academia: string
}

type ServiceInfo = {
  title: string
  description: string
}

type RamalInfo = {
  title: string
  ramal: string
}

type HotelInfoContentType = {
  contact: ContactInfo
  hours: HoursInfo
  ramal: RamalInfo[]
  services: ServiceInfo[]
}

type HotelInfoData = {
  id?: number
  content: HotelInfoContentType
}

const HotelInfoContent: FC = () => {
  const {t} = i18n
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('contact')
  const [hotelInfo, setHotelInfo] = useState<HotelInfoData>({
    content: {
      contact: {
        phone: '',
        whatsapp: '',
        email: '',
      },
      ramal: [],
      hours: {
        recepção: '',
        restaurante: '',
        piscina: '',
        spa: '',
        academia: '',
      },
      services: [],
    },
  })

  // Carregar dados do hotel
  useEffect(() => {
    const loadHotelInfo = async () => {
      try {
        setLoading(true)
        const response = await GET('/hotel-info')
        if (response.success && response.data && response.data[0]) {
          const data: any = response.data[0]
          if (!data.content.ramal || data.content.ramal.length === 0) {
            data.content.ramal = []
          }
          data.content.services = data.hotelInfosService

          setHotelInfo(data)
        } else {
          // Garante que o estado nunca será nulo/undefined
          setHotelInfo({
            content: {
              contact: {phone: '', whatsapp: '', email: ''},
              hours: {recepção: '', restaurante: '', piscina: '', spa: '', academia: ''},
              ramal: [],
              services: [{title: '', description: ''}],
            },
          })
        }
      } catch (error) {
        console.error('Erro ao carregar informações do hotel:', error)
        // Em caso de erro, também mantém o estado válido
        setHotelInfo({
          content: {
            contact: {phone: '', whatsapp: '', email: ''},
            hours: {recepção: '', restaurante: '', piscina: '', spa: '', academia: ''},
            ramal: [],
            services: [{title: '', description: ''}],
          },
        })
      } finally {
        setLoading(false)
      }
    }
    loadHotelInfo()
  }, [])

  const handleContactChange = (field: keyof ContactInfo, value: string) => {
    setHotelInfo((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        contact: {
          ...prev.content.contact,
          [field]: value,
        },
      },
    }))
  }

  const handleHoursChange = (field: keyof HoursInfo, value: string) => {
    setHotelInfo((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        hours: {
          ...prev.content.hours,
          [field]: value,
        },
      },
    }))
  }

  const handleServiceChange = (index: number, field: keyof ServiceInfo, value: string) => {
    setHotelInfo((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        services: prev.content.services.map((service, i) =>
          i === index ? {...service, [field]: value} : service
        ),
      },
    }))
  }

  const addService = () => {
    setHotelInfo((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        services: [...prev.content.services, {title: '', description: ''}],
      },
    }))
  }

  const removeService = (index: number) => {
    if (hotelInfo.content.services.length > 1) {
      setHotelInfo((prev) => ({
        ...prev,
        content: {
          ...prev.content,
          services: prev.content.services.filter((_, i) => i !== index),
        },
      }))
    }
  }

  const handleRamalChange = (index: number, field: keyof RamalInfo, value: string) => {
    setHotelInfo((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        ramal: prev.content.ramal.map((ramal, i) =>
          i === index ? {...ramal, [field]: value} : ramal
        ),
      },
    }))
  }

  const addRamal = () => {
    setHotelInfo((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        ramal: [...prev.content.ramal, {title: '', ramal: ''}],
      },
    }))
  }

  const removeRamal = (index: number) => {
    if (hotelInfo.content.ramal.length > 1) {
      setHotelInfo((prev) => ({
        ...prev,
        content: {
          ...prev.content,
          ramal: prev.content.ramal.filter((_, i) => i !== index),
        },
      }))
    }
  }

  const validateForm = () => {
    // Validação de contato
    if (!hotelInfo.content.contact.phone.trim()) {
      Swal.fire({
        icon: 'error',
        title: `${t('hospitality_info.messages.error_title')}`,
        text: `${t('hospitality_info.messages.required_phone')}`,
      })
      setActiveTab('contact')
      return false
    }

    if (!hotelInfo.content.contact.email.trim()) {
      Swal.fire({
        icon: 'error',
        title: `${t('hospitality_info.messages.error_title')}`,
        text: `${t('hospitality_info.messages.required_email')}`,
      })
      setActiveTab('contact')
      return false
    }

    // Validação de horários
    const requiredHours = ['recepção', 'restaurante', 'piscina'] as const
    for (const hour of requiredHours) {
      if (!hotelInfo.content.hours[hour].trim()) {
        Swal.fire({
          icon: 'error',
          title: `${t('hospitality_info.messages.error_title')}`,
          text: `${t('hospitality_info.messages.required_hours', {
            hour: t(`hospitality_info.hours.${hour}_label`),
          })}`,
        })
        setActiveTab('hours')
        return false
      }
    }

    // Validação de serviços
    const validServices = hotelInfo.content.services.filter(
      (service) => service.title.trim() && service.description.trim()
    )
    if (validServices.length < hotelInfo.content.services.length) {
      Swal.fire({
        icon: 'error',
        title: `${t('hospitality_info.messages.error_title')}`,
        text: `${t('hospitality_info.messages.required_service')}`,
      })
      setActiveTab('services')
      return false
    }

    // Validação de ramais
    const validRamais = hotelInfo.content.ramal.filter(
      (ramal) => ramal.title.trim() && ramal.ramal.trim()
    )
    if (validRamais.length < hotelInfo.content.ramal.length) {
      Swal.fire({
        icon: 'error',
        title: `${t('hospitality_info.messages.error_title')}`,
        text: `${t('hospitality_info.messages.required_ramal')}`,
      })
      setActiveTab('ramal')
      return false
    }

    return true
  }

  const saveHotelInfo = async () => {
    if (!validateForm()) return

    try {
      setLoading(true)

      // Filtrar serviços vazios
      const filteredServices = hotelInfo.content.services.filter(
        (service) => service.title.trim() && service.description.trim()
      )

      const payload = {
        content: {
          ...hotelInfo.content,
          services: filteredServices,
        },
      }

      const response = await POST('/hotel-info', payload)

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: `${t('hospitality_info.messages.success_title')}`,
          text: `${t('hospitality_info.messages.success_text')}`,
        })

        if (!hotelInfo.id && response.data) {
          setHotelInfo((prev) => ({...prev, id: response.data.id}))
        }
      } else {
        throw new Error(response.message || t('hospitality_info.messages.error_text'))
      }
    } catch (error: any) {
      const msg = error.message || `${t('hospitality_info.messages.error_text')}`
      Swal.fire({
        icon: 'error',
        title: `${t('hospitality_info.messages.error_title')}`,
        text: msg,
      })
    } finally {
      setLoading(false)
    }
  }

  // Funções de renderização para cada tab
  const renderContactTab = () => (
    <div className='row'>
      <div className='col-lg-6'>
        <Input
          label={t('hospitality_info.contact.phone_label')}
          inputAttr={{
            type: 'tel',
            placeholder: t('hospitality_info.contact.phone_placeholder'),
          }}
          placeholder={t('hospitality_info.contact.phone_placeholder')}
          name='phone'
          value={hotelInfo.content.contact.phone}
          change={(value: string) => handleContactChange('phone', value)}
        />
      </div>
      <div className='col-lg-6'>
        <Input
          label={t('hospitality_info.contact.whatsapp_label')}
          inputAttr={{
            type: 'tel',
            placeholder: t('hospitality_info.contact.whatsapp_placeholder'),
          }}
          placeholder={t('hospitality_info.contact.whatsapp_placeholder')}
          name='whatsapp'
          value={hotelInfo.content.contact.whatsapp}
          change={(value: string) => handleContactChange('whatsapp', value)}
        />
      </div>
      <div className='col-lg-12'>
        <Input
          label={t('hospitality_info.contact.email_label')}
          inputAttr={{
            type: 'email',
            placeholder: t('hospitality_info.contact.email_placeholder'),
          }}
          placeholder={t('hospitality_info.contact.email_placeholder')}
          name='email'
          value={hotelInfo.content.contact.email}
          change={(value: string) => handleContactChange('email', value)}
        />
      </div>
    </div>
  )

  const renderHoursTab = () => (
    <>
      <div className='row'>
        <div className='col-lg-6'>
          <Input
            label={t('hospitality_info.hours.recepcao_label')}
            inputAttr={{
              type: 'text',
            }}
            placeholder={t('hospitality_info.hours.recepcao_placeholder')}
            name='recepcao'
            value={hotelInfo.content.hours.recepção}
            change={(value: string) => handleHoursChange('recepção', value)}
          />
        </div>
        <div className='col-lg-6'>
          <Input
            label={t('hospitality_info.hours.restaurante_label')}
            inputAttr={{
              type: 'text',
            }}
            placeholder={t('hospitality_info.hours.restaurante_placeholder')}
            name='restaurante'
            value={hotelInfo.content.hours.restaurante}
            change={(value: string) => handleHoursChange('restaurante', value)}
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-lg-6'>
          <Input
            label={t('hospitality_info.hours.piscina_label')}
            inputAttr={{
              type: 'text',
            }}
            placeholder={t('hospitality_info.hours.piscina_placeholder')}
            name='piscina'
            value={hotelInfo.content.hours.piscina}
            change={(value: string) => handleHoursChange('piscina', value)}
          />
        </div>
        <div className='col-lg-6'>
          <Input
            label={t('hospitality_info.hours.spa_label')}
            inputAttr={{
              type: 'text',
              placeholder: t('hospitality_info.hours.spa_placeholder'),
            }}
            name='spa'
            placeholder={t('hospitality_info.hours.spa_placeholder')}
            value={hotelInfo.content.hours.spa}
            change={(value: string) => handleHoursChange('spa', value)}
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-lg-6'>
          <Input
            label={t('hospitality_info.hours.academia_label')}
            inputAttr={{
              type: 'text',
              placeholder: t('hospitality_info.hours.academia_placeholder'),
            }}
            placeholder={t('hospitality_info.hours.academia_placeholder')}
            name='academia'
            value={hotelInfo.content.hours.academia}
            change={(value: string) => handleHoursChange('academia', value)}
          />
        </div>
      </div>
    </>
  )

  const renderRamalTab = () => (
    <>
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <h5 className='mb-0'>{t('hospitality_info.ramal.title')}</h5>
        <Button
          text={t('hospitality_info.ramal.add_button')}
          color='primary'
          size='small'
          click={addRamal}
          icon='fas fa-plus'
        />
      </div>

      {hotelInfo.content.ramal.map((ramal, index) => (
        <div key={index} className='border rounded p-4 mb-4 bg-light-primary'>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <h6 className='mb-0'>{`${t('hospitality_info.ramal.ramal_number')} ${index + 1}`}</h6>
            {hotelInfo.content.ramal.length > 1 && (
              <Button
                text={t('hospitality_info.ramal.remove_button')}
                color='danger'
                size='small'
                click={() => removeRamal(index)}
                icon='fas fa-trash'
              />
            )}
          </div>
          <div className='row'>
            <div className='col-lg-4'>
              <Input
                label={`${t('hospitality_info.ramal.ramal_title_label')}${index === 0 ? ' *' : ''}`}
                inputAttr={{
                  type: 'text',
                  placeholder: t('hospitality_info.ramal.ramal_title_placeholder'),
                }}
                name={`ramal-title-${index}`}
                value={ramal.title}
                change={(value: string) => handleRamalChange(index, 'title', value)}
              />
            </div>
            <div className='col-lg-8'>
              <Input
                label={`${t('hospitality_info.ramal.ramal_description_label')}${
                  index === 0 ? ' *' : ''
                }`}
                inputAttr={{
                  type: 'text',
                  placeholder: t('hospitality_info.ramal.ramal_description_placeholder'),
                }}
                name={`ramal-description-${index}`}
                value={ramal.ramal}
                change={(value: string) => handleRamalChange(index, 'ramal', value)}
              />
            </div>
          </div>
        </div>
      ))}
    </>
  )

  const renderServicesTab = () => (
    <>
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <h5 className='mb-0'>{t('hospitality_info.services.title')}</h5>
        <Button
          text={t('hospitality_info.services.add_button')}
          color='primary'
          size='small'
          click={addService}
          icon='fas fa-plus'
        />
      </div>

      {hotelInfo.content.services.map((service, index) => (
        <div key={index} className='border rounded p-4 mb-4'>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <h6 className='mb-0'>{`${t('hospitality_info.services.service_number')} ${
              index + 1
            }`}</h6>
            {hotelInfo.content.services.length > 1 && (
              <Button
                text={t('hospitality_info.services.remove_button')}
                color='danger'
                size='small'
                click={() => removeService(index)}
                icon='fas fa-trash'
              />
            )}
          </div>
          <div className='row'>
            <div className='col-lg-12'>
              <Input
                label={`${t('hospitality_info.services.service_title_label')}${
                  index === 0 ? ' *' : ''
                }`}
                inputAttr={{
                  type: 'text',
                  placeholder: t('hospitality_info.services.service_title_placeholder'),
                }}
                name={`service-title-${index}`}
                value={service.title}
                change={(value: string) => handleServiceChange(index, 'title', value)}
              />
            </div>
            <div className='col-lg-12'>
              <EditorQuill
                value={service.description}
                label={t('hospitality_info.services.service_description_label')}
                placeholder={t('hospitality_info.services.service_description_placeholder')}
                change={(value: string) => handleServiceChange(index, 'description', value)}
                maxLength={5000} // Limite de 5000 caracteres
              />
            </div>
          </div>
        </div>
      ))}
    </>
  )

  // Configuração das tabs
  const tabs: TabItem[] = [
    {
      id: 'contact',
      label: t('hospitality_info.tabs.contact'),
      icon: 'fas fa-phone',
      content: renderContactTab(),
    },
    {
      id: 'ramal',
      label: t('hospitality_info.tabs.ramal'),
      icon: 'fas fa-phone',
      content: renderRamalTab(),
    },
    {
      id: 'hours',
      label: t('hospitality_info.tabs.hours'),
      icon: 'fas fa-clock',
      content: renderHoursTab(),
    },
    {
      id: 'services',
      label: t('hospitality_info.tabs.services'),
      icon: 'fas fa-concierge-bell',
      content: renderServicesTab(),
    },
  ]

  if (loading) {
    return <Loading />
  }

  return (
    <div className='row g-5 gx-xxl-12'>
      <div className='d-flex justify-content-between flex-wrap'>
        <div style={{flex: 1}}>
          <div className='w-100 card'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {t('hospitality_info.page_title')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {t('hospitality_info.page_subtitle')}
                </span>
              </h3>
            </div>
            <div className='card-body'>
              {/* Componente de Tabs Reutilizável */}
              <HospitalityTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

              {/* Actions */}
              <div className='d-flex justify-content-end mt-6'>
                <Button
                  text={t('hospitality_info.actions.save_button')}
                  color='primary'
                  size='small'
                  click={saveHotelInfo}
                  icon='fas fa-save'
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const HotelInfo: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: true},
          {title: 'Hospitality', path: 'hospitality', isActive: true},
          {title: t('hospitality_info.page_title'), path: 'hospitality/info', isActive: true},
        ]}
      >
        {t('hospitality_info.page_title')}
      </PageTitle>
      <HotelInfoContent />
    </>
  )
}

export {HotelInfo}
